import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import Badge from "../components/ui/Badge";
import styles from "./Clients.module.css";

const SERVICE_TYPES = [
  "Pay While Working - Europe",
  "Gulf Job Opportunities",
  "Canada Visa Application",
];

const PROCESS_STAGES = [
  "Registered",
  "Documentation Pending",
  "Documents Submitted",
  "Documentation Done",
  "Offer Letter Received",
  "Offer Letter Signing",
  "Offer Letter Signed",
  "Awaiting Contract",
  "Appointments Booked",
  "Awaiting Appointment",
  "Biometrics Done",
  "Awaiting Confirmation",
  "Visa Approved",
  "Visa Denial",
  "Departed",
];

const STAGE_COLORS = {
  "Registered":            { bg: "#f7fafc", color: "#4a5568" },
  "Documentation Pending": { bg: "#fff5f7", color: "#97266d" },
  "Documents Submitted":   { bg: "#ebf8ff", color: "#2b6cb0" },
  "Documentation Done":    { bg: "#f0fff4", color: "#276749" },
  "Offer Letter Received": { bg: "#faf5ff", color: "#6b46c1" },
  "Offer Letter Signing":  { bg: "#fffaf0", color: "#c05621" },
  "Offer Letter Signed":   { bg: "#f0fff4", color: "#22543d" },
  "Awaiting Contract":     { bg: "#fffaf0", color: "#c05621" },
  "Appointments Booked":   { bg: "#ebf8ff", color: "#2c5282" },
  "Awaiting Appointment":  { bg: "#fff5f7", color: "#97266d" },
  "Biometrics Done":       { bg: "#faf5ff", color: "#553c9a" },
  "Awaiting Confirmation": { bg: "#fffaf0", color: "#c05621" },
  "Visa Approved":         { bg: "#f0fff4", color: "#22543d" },
  "Visa Denial":           { bg: "#fff5f5", color: "#c53030" },
  "Departed":              { bg: "#0a1628", color: "#fff"    },
};

const fmt = (n) =>
  `KES ${Number(n ?? 0).toLocaleString("en-KE")}`;

export default function Clients() {
  const { isAdmin, profile } = useAuth();
  const navigate = useNavigate();
  const [clients,         setClients]         = useState([]);
  const [employees,       setEmployees]        = useState([]);
  const [loading,         setLoading]          = useState(true);
  const [search,          setSearch]           = useState("");
  const [filterSvc,       setFilterSvc]        = useState("");
  const [filterStatus,    setFilterStatus]     = useState("");
  const [filterStage,     setFilterStage]      = useState("");
  const [filterConsultant,setFilterConsultant] = useState("");

  useEffect(() => { if (profile) load(); }, [profile]); // eslint-disable-line

  async function load() {
    setLoading(true);
    let q = supabase
      .from("clients")
      .select("*, consultant:employees(id, full_name)")
      .order("created_at", { ascending: false });
    if (!isAdmin) q = q.eq("consultant_id", profile.id);

    const [{ data: cd }, { data: ed }] = await Promise.all([
      q,
      isAdmin
        ? supabase.from("employees").select("id, full_name").eq("role","staff").order("full_name")
        : Promise.resolve({ data: [] }),
    ]);
    setClients(cd ?? []);
    setEmployees(ed ?? []);
    setLoading(false);
  }

  async function deleteClient(id) {
    if (!window.confirm("Delete this client? This cannot be undone.")) return;
    await supabase.from("clients").delete().eq("id", id);
    setClients((prev) => prev.filter((c) => c.id !== id));
  }

  const filtered = clients.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      c.full_name.toLowerCase().includes(q) ||
      c.phone.includes(q) ||
      (c.email ?? "").toLowerCase().includes(q) ||
      (c.consultant?.full_name ?? "").toLowerCase().includes(q);
    const matchSvc        = !filterSvc        || c.service_type     === filterSvc;
    const matchStatus     = !filterStatus     || c.status           === filterStatus;
    const matchStage      = !filterStage      || c.process_stage    === filterStage;
    const matchConsultant = !filterConsultant || c.consultant?.id   === filterConsultant;
    return matchSearch && matchSvc && matchStatus && matchStage && matchConsultant;
  });

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          className={styles.search}
          placeholder="Search name, phone, email or consultant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filters}>
          <select className={styles.filter} value={filterSvc}
            onChange={(e) => setFilterSvc(e.target.value)}>
            <option value="">All Services</option>
            {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
          </select>

          {isAdmin && (
            <select className={styles.filter} value={filterConsultant}
              onChange={(e) => setFilterConsultant(e.target.value)}>
              <option value="">All Consultants</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.full_name}</option>
              ))}
            </select>
          )}

          <select className={styles.filter} value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}>
            <option value="">All Stages</option>
            {PROCESS_STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>

          <select className={styles.filter} value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {["Active","Completed","On Hold","Cancelled"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>

        <button className={styles.addBtn} onClick={() => navigate("/clients/new")}>
          + Add Client
        </button>
      </div>

      {/* Table */}
      {/* Table & Mobile Cards */}
      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.empty}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>No clients found.</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Process Stage</th>
                  {isAdmin && <th>Consultant</th>}
                  <th>Total</th>
                  <th>Received</th>
                  <th>Balance</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const stageStyle = STAGE_COLORS[c.process_stage] ?? { bg:"#f7fafc", color:"#4a5568" };
                  return (
                    <tr key={c.id} className={styles.row}>
                      <td>
                        <strong>{c.full_name}</strong>
                        <div className={styles.sub}>{c.phone}</div>
                        {c.email && <div className={styles.sub}>{c.email}</div>}
                      </td>
                      <td>
                        <div style={{ fontSize: 12.5 }}>{c.service_type}</div>
                        {c.destination_country &&
                          <div className={styles.sub}>{c.destination_country}</div>}
                      </td>
                      <td>
                        <span
                          className={styles.stagePill}
                          style={{ background: stageStyle.bg, color: stageStyle.color }}
                        >
                          {c.process_stage ?? "Registered"}
                        </span>
                      </td>
                      {isAdmin && <td>{c.consultant?.full_name ?? "—"}</td>}
                      <td>{fmt(c.total_amount)}</td>
                      <td className={styles.green}>{fmt(c.amount_received)}</td>
                      <td><strong>{fmt(c.balance_due)}</strong></td>
                      <td><Badge label={c.payment_status} /></td>
                      <td><Badge label={c.status} /></td>
                      <td style={{ fontSize:12, whiteSpace:"nowrap" }}>
                        {c.date_started ?? "—"}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button className={styles.editBtn}
                            onClick={() => navigate(`/clients/${c.id}/edit`)}>
                            Edit
                          </button>
                          {isAdmin && (
                            <button className={styles.delBtn}
                              onClick={() => deleteClient(c.id)}>
                              Del
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Card List */}
            <div className={styles.mobileList}>
              {filtered.map((c) => {
                const stageStyle = STAGE_COLORS[c.process_stage] ?? { bg: "#f7fafc", color: "#4a5568" };
                return (
                  <div key={c.id} className={styles.clientCard}>
                    <div className={styles.cardHeader}>
                      <div>
                        <div className={styles.cardName}>{c.full_name}</div>
                        <div className={styles.cardContact}>
                          <a href={`tel:${c.phone}`} className={styles.phoneLink} onClick={(e) => e.stopPropagation()}>
                            📞 {c.phone}
                          </a>
                          {c.email && (
                            <span className={styles.emailText}>
                              · <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>{c.email}</a>
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge label={c.status} />
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardDetailRow}>
                        <span className={styles.detailLabel}>Service:</span>
                        <span className={styles.detailVal}>
                          {c.service_type}
                          {c.destination_country && ` (${c.destination_country})`}
                        </span>
                      </div>
                      <div className={styles.cardDetailRow}>
                        <span className={styles.detailLabel}>Stage:</span>
                        <span
                          className={styles.stagePill}
                          style={{ background: stageStyle.bg, color: stageStyle.color }}
                        >
                          {c.process_stage ?? "Registered"}
                        </span>
                      </div>
                      {isAdmin && c.consultant?.full_name && (
                        <div className={styles.cardDetailRow}>
                          <span className={styles.detailLabel}>Consultant:</span>
                          <span className={styles.detailVal}>{c.consultant.full_name}</span>
                        </div>
                      )}
                      {c.date_started && (
                        <div className={styles.cardDetailRow}>
                          <span className={styles.detailLabel}>Started:</span>
                          <span className={styles.detailVal}>{c.date_started}</span>
                        </div>
                      )}
                    </div>

                    {/* Financial Summary */}
                    <div className={styles.cardFinances}>
                      <div className={styles.finCol}>
                        <span className={styles.finLabel}>Total</span>
                        <span className={styles.finVal}>{fmt(c.total_amount)}</span>
                      </div>
                      <div className={styles.finCol}>
                        <span className={styles.finLabel}>Received</span>
                        <span className={`${styles.finVal} ${styles.green}`}>{fmt(c.amount_received)}</span>
                      </div>
                      <div className={styles.finCol}>
                        <span className={styles.finLabel}>Balance</span>
                        <span className={`${styles.finVal} ${Number(c.balance_due) > 0 ? styles.orange : ""}`}>
                          {fmt(c.balance_due)}
                        </span>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <div>
                        <Badge label={c.payment_status} />
                      </div>
                      <div className={styles.cardActions}>
                        <button
                          className={styles.editBtnMobile}
                          onClick={() => navigate(`/clients/${c.id}/edit`)}
                        >
                          Edit Client
                        </button>
                        {isAdmin && (
                          <button
                            className={styles.delBtnMobile}
                            onClick={() => deleteClient(c.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className={styles.count}>
        {filtered.length} client{filtered.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}